import { RefObject, useCallback, useEffect, useState } from 'react';
import { useMounted } from 'src/hooks/use-mounted';

function withinContainer(
  ref: RefObject<HTMLElement>,
  target: EventTarget | null
) {
  return Boolean(
    ref.current && target instanceof Node && ref.current.contains(target)
  );
}

type WhileActiveOptions = {
  initialActive?: boolean;
};

export function useWhileActive(
  ref: RefObject<HTMLElement | undefined>,
  { initialActive = false }: WhileActiveOptions = {}
) {
  const [active, setActive] = useState(initialActive);

  const [angularIframe] = useState(() => {
    const [iframe] = document.getElementsByName('embedded-angular');
    return iframe as HTMLIFrameElement | undefined;
  });

  const [mousedownInside, setMousedownInside] = useState(false);
  const handleMousedown = useCallback(
    (event: MouseEvent) => {
      if (!ref.current) return;
      if (withinContainer(ref as RefObject<HTMLElement>, event.target)) {
        setMousedownInside(true);
      } else {
        setMousedownInside(false);
        setActive(false);
      }
    },
    [ref]
  );

  const handleFocusout = useCallback(
    (event: FocusEvent) => {
      if (!ref.current) return;
      // ignore switching to something completely different (e.g. dev tools)
      if (!document.hasFocus()) return;
      // ignore focus switch within the container
      if (withinContainer(ref as RefObject<HTMLElement>, event.relatedTarget))
        return;
      if (withinContainer(ref as RefObject<HTMLElement>, event.target)) return;
      // ignore focus loss, if clicked within the container
      if (!event.relatedTarget && mousedownInside) return;

      setActive(false);
    },
    [mousedownInside, ref]
  );

  const mounted = useMounted();

  // if we tab away, we'll set active to false
  // NOTE!!! why this complicated way? previously we used "focusin" to
  // check if the element which gets the focus next is within "ref",
  // but this sadly messed with scroll events
  const handleKeydown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        setTimeout(() => {
          if (!mounted.current) return;
          if (!ref.current?.contains(document.activeElement)) setActive(false);
        }, 1);
      }
    },
    [mounted, ref]
  );

  useEffect(() => {
    if (active) {
      document.addEventListener('focusout', handleFocusout);
      document.addEventListener('mousedown', handleMousedown);
      angularIframe?.contentDocument?.addEventListener(
        'mousedown',
        handleMousedown
      );
      document.addEventListener('keydown', handleKeydown, { capture: true });
      return () => {
        document.removeEventListener('focusout', handleFocusout);
        document.removeEventListener('mousedown', handleMousedown);
        angularIframe?.contentDocument?.removeEventListener(
          'mousedown',
          handleMousedown
        );
        document.removeEventListener('keydown', handleKeydown);
      };
    }
  }, [angularIframe, active, handleFocusout, handleMousedown, handleKeydown]);

  return [active, setActive] as const;
}
